import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, InputText } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { NativeDateCalendarPopupTrigger } from '@PetSureAustralia/petsure-react-ui-library/distLib/components/Forms/FormElements/SpecialInputs/Birthdates/NativeDateCalendarPopupTrigger';
import { useFormContext } from 'react-hook-form';
import { DateToShortIsoString, IsValidDate } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { PetFilter } from './petFilter';
import { FullPetDetailsPropTypes } from '../../propTypes/petDetails';
import { TableFilterPropTypes } from '../../propTypes/documents';
import { limitYearToFourDigits } from '../../utils/date';
import { revertLegacyPolicyNumberFormat } from '../../helpers/paymentHelper';
import './tableFilters.scss';

export const TableFilters = ({
  activeFilters,
  onFilterChange,
  petDetails,
  tableId,
  typeFilterOptions = [],
  useGlobalPetSelector = false,
  minDate,
  maxDate,
  currentDate
}) => {
  const fieldFormContext = useFormContext();
  const fromDateId = `dateFrom_${tableId}`;
  const toDateId = `dateTo_${tableId}`;
  const typeDropDownId = `typeSelect_${tableId}`;
  const [fromDateInput, setFromDateInput] = useState(DateToShortIsoString(activeFilters.fromDate) || '');
  const [toDateInput, setToDateInput] = useState(DateToShortIsoString(activeFilters.toDate) || '');

  const handleFilterChange = (newFilterValue) => {
    const newFilterState = { ...activeFilters, ...newFilterValue };
    onFilterChange({ filterState: newFilterState });
  };

  const handlePetFilterChange = (event) => {
    handleFilterChange({ policy: event.target.value });
  };

  const handleDateChange = (setter, filterKey) => (event) => {
    const dateValue = limitYearToFourDigits(event.target.value);
    setter(dateValue); // keep what user typed

    if (IsValidDate(dateValue)) {
      handleFilterChange({ [filterKey]: new Date(dateValue) });
    } else if (!dateValue) {
      handleFilterChange({ [filterKey]: null });
    }
  };

  const handleTypeChange = (event) => {
    handleFilterChange({ type: event.target.value });
  };

  return (
    <div className="petDocumentTableFilters">
      <div className="petSureForm labelInsetFixed">
        <fieldset
          className="documentFilterContainer"
          aria-label={`Claim documents date filter for table ${tableId}`}
          role="group"
        >
          <h5 aria-hidden="true">Filter</h5>

          <InputText
            name={fromDateId}
            inputType="date"
            label="From"
            labelIsInset
            placeholder="dd/mm/yyyy"
            value={fromDateInput}
            min={DateToShortIsoString(minDate)}
            fieldFormContext={fieldFormContext}
            customChangeHandler={handleDateChange(setFromDateInput, 'fromDate')}
            customCssClass="nativeDateInput"
            customPostInputContent={<NativeDateCalendarPopupTrigger dateInputElementId={fromDateId} />}
          />

          <InputText
            name={toDateId}
            inputType="date"
            label="To"
            labelIsInset
            placeholder="dd/mm/yyyy"
            value={toDateInput}
            max={DateToShortIsoString(maxDate)}
            fieldFormContext={fieldFormContext}
            customChangeHandler={handleDateChange(setToDateInput, 'toDate')}
            customCssClass="nativeDateInput"
            customPostInputContent={<NativeDateCalendarPopupTrigger dateInputElementId={toDateId} />}
          />
        </fieldset>
      </div>

      <PetFilter
        isLabelHidden
        petDetails={petDetails}
        onPetFilterChange={handlePetFilterChange}
        policyFilter={revertLegacyPolicyNumberFormat(activeFilters.policy)}
        filterName={`petFilter_${tableId}`}
      />

      {typeFilterOptions.length > 0 && (
        <Dropdown
          fieldFormContext={fieldFormContext}
          name={typeDropDownId}
          value={activeFilters.type}
          options={typeFilterOptions}
          label="Filter claims by pet"
          hideLabel
          customCssClass="typeFilterDropdown"
          customChangeHandler={handleTypeChange}
          includeEmptyOption={false}
        />
      )}
    </div>
  );
};

TableFilters.propTypes = {
  petDetails: PropTypes.arrayOf(FullPetDetailsPropTypes),
  activeFilters: TableFilterPropTypes,
  onFilterChange: PropTypes.func,
  tableId: PropTypes.string,
  typeFilterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  useGlobalPetSelector: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  currentDate: PropTypes.instanceOf(Date)
};
